import { useState } from 'react';
import { useSelector } from 'react-redux';

import {
  getNotificationListQueryKeyPrefix,
  queryClient,
  useGetActionsDashboard,
} from '@frontend/api';
import {
  Box,
  Button,
  Heading,
  Container,
  ContainerHeader,
  ContainerBody,
  ContainerFooter,
  Center,
  VStack,
  ReqsReviewFeatureIcon,
  CheckmarkIcon,
  NotificationsIcon,
  Pagination,
  useMediaQuery,
  Spinner,
} from '@frontend/components';

import { PERMISSIONS, SUPPLIER_ROLES } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import MarkAllNotificationsReadButton from 'modules/notifications/components/MarkAllNotificationsReadButton';
import NotificationsList from 'modules/notifications/components/NotificationsList';
import { SearchBarLarge } from 'modules/search/SearchBarLarge';
import TaskList from 'modules/tasks/dashboard/Dashboard';
import { TASK_STATUSES } from 'modules/tasks/data/constants';

import { useFeatureFlagValue } from 'store/session/hooks';
import {
  getGroupMemberSupplierRoles,
  getUserId,
} from 'store/session/selectors';

import { SearchDataContextProvider } from '../search/context';
import { ReqsReviewActionsList } from './components/ReqsReviewActionsList';
import WorkflowDashboard from './workflows/WorkflowDashboard';

const MODULE_PROPS = {
  size: 'md' as const,
  sx: {
    justifySelf: 'stretch',
    alignSelf: 'stretch',
    overflow: 'hidden',
    flexBasis: 'fit-content',
    "[class*='PartNumberRow'], [class*='StatusRow']": {
      flexWrap: 'wrap',
    },
  },
};

const WORKFLOWS_TABLE_HEIGHT = 540;

const SingleSiteDashboard = () => {
  const activeUserId = useSelector(getUserId);
  const isQuotingToolAvailable = useFeatureFlagValue(
    featureFlagKeys.isQuotingToolAvailable,
  );
  const isTasksAvailable = useFeatureFlagValue(
    featureFlagKeys.isTasksAvailable,
  );
  const isRequirementsReviewEnabled = useFeatureFlagValue(
    featureFlagKeys.isRequirementsReviewEnabled,
  );

  const [isBrowserHeightSmall] = useMediaQuery(
    `(max-height: ${WORKFLOWS_TABLE_HEIGHT * 2}px)`,
  );

  const groupMemberSupportRoles = useSelector(getGroupMemberSupplierRoles);
  const canUserSeeQuotes =
    groupMemberSupportRoles[SUPPLIER_ROLES.QUOTES] !== PERMISSIONS.NO_ACCESS;

  const shouldShowWorkflowDashboard =
    isQuotingToolAvailable && canUserSeeQuotes;
  const shouldShowRequirementsReview =
    isRequirementsReviewEnabled && canUserSeeQuotes;

  const [requirementsReviewPage, setRequirementsReviewPage] = useState(1);
  const requirementsReviewPageSize = 10;

  const { data: actionsDashboardResult, isLoading: isLoadingActionsDashboard } =
    useGetActionsDashboard(
      {
        page: requirementsReviewPage,
        pageSize: requirementsReviewPageSize,
      },
      {
        queryOptions: {
          enabled: shouldShowRequirementsReview,
          keepPreviousData: true,
        },
      },
    );

  return (
    <VStack
      data-testid={'home-page'}
      height={{
        base: 'auto',
        tablet: 'auto',
        desktop: isBrowserHeightSmall ? 'auto' : '100%',
      }}
      overflow="clip"
      alignItems="stretch"
      zIndex={1}
      position="relative"
    >
      {isQuotingToolAvailable && (
        <Center
          flex="0 0 auto"
          bg="page.plateLight"
          p={4}
          borderBottom="1px solid"
          borderColor="separator.divider"
        >
          <Box width="100%" maxWidth="1200px">
            <SearchDataContextProvider>
              <SearchBarLarge />
            </SearchDataContextProvider>
          </Box>
        </Center>
      )}
      {/*
      This grid layout is pretty hairy, to solve for the following problems...
      1. Users can have many of the individual elements on or off
      2. The workflows table is a fixed height, and needs width
      3. The other modules need to fill the available space
      3. We don't want to change things much for current users at the time of editing

      AFAIK normal flex wouldn't cut it for this layout, because we need to stretch
      and plce content along both axes.

      If you edit this, test with the following...
      - Turn off quote workflow dashboard
      - Turn off 1, 2, and 3 of the other containers

      IN EACH CASE, test WIDE, NARROW, SHORT, and TALL browsers, and all combinations.
       */}
      <Box
        padding={{ base: 1.5, tablet: 2 }}
        overflow={isBrowserHeightSmall ? undefined : 'hidden'}
        flex={isBrowserHeightSmall ? undefined : '1 1 100%'}
        display="grid"
        gap={{ base: 1, tablet: 2 }}
        gridAutoFlow="row dense"
        sx={{
          ...(shouldShowWorkflowDashboard
            ? {
                // If workflow dashboard is shown
                gridTemplateColumns: {
                  base: 'auto',
                  tablet: 'auto',
                  desktop: '1fr 1fr 500px',
                },
                gridAutoRows: {
                  base: 'auto minmax(max-content, 60vh)',
                  tablet: 'auto 1fr',
                },
                '.workflow-table': {
                  gridColumn: {
                    base: '1',
                    tablet: '1',
                    desktop: '1 / span 2',
                  },
                  gridRow: {
                    base: '1',
                    tablet: '1',
                    desktop: '1',
                  },
                },
                ".workflow-table + [class*='-container']": {
                  // Slot the next container in alongside the workflow table
                  gridRow: {
                    base: 'auto',
                    tablet: 'auto',
                    desktop: '1 / span 2',
                  },
                  gridColumn: {
                    base: 'auto',
                    tablet: 'auto',
                    desktop: '3',
                  },
                },
                "[class*='-container']:nth-child(3):last-child": {
                  gridColumn: {
                    desktop: '1 / span 2',
                  },
                },
              }
            : {
                gridTemplateColumns: {
                  // If no workflow dashboard is shown
                  base: '1fr',
                  desktop: 'repeat(3, 1fr)',
                },
                gridAutoRows: 'auto',
              }),
        }}
      >
        {shouldShowWorkflowDashboard && <WorkflowDashboard />}

        <Container {...MODULE_PROPS} className="notifications-container">
          <ContainerHeader
            justifyContent="flex-start"
            gap={1}
            alignItems="center"
          >
            <NotificationsIcon color="feature.secondary" boxSize={2.5} />
            <Heading size="lg">Notifications</Heading>
            <MarkAllNotificationsReadButton
              callback={() => {
                queryClient.invalidateQueries({
                  queryKey: getNotificationListQueryKeyPrefix(),
                });
              }}
            />
          </ContainerHeader>
          <ContainerBody
            flex="1 1 100%"
            overflowY="auto"
            p={0}
            flexDir="column"
          >
            <NotificationsList />
          </ContainerBody>
          <ContainerFooter justifyContent="center">
            <Button variant="link" size="md" as="a" href={'/notifications'}>
              View all notifications
            </Button>
          </ContainerFooter>
        </Container>

        {isTasksAvailable && (
          <Container {...MODULE_PROPS} className="tasks-container">
            <ContainerHeader
              justifyContent="flex-start"
              gap={1}
              alignItems="center"
            >
              <CheckmarkIcon color="feature.secondary" boxSize={2.5} />
              <Heading size="lg">Tasks assigned to me</Heading>
            </ContainerHeader>
            <ContainerBody flex="1 1 100%" overflowY="auto" p={0}>
              <TaskList />
            </ContainerBody>
            <ContainerFooter justifyContent="center">
              <Button
                variant="link"
                size="md"
                as="a"
                href={`/tasks?assigned_to_in=${activeUserId}&status_in=${TASK_STATUSES.OPEN},${TASK_STATUSES.OVERDUE}`}
              >
                View all tasks
              </Button>
            </ContainerFooter>
          </Container>
        )}
        {shouldShowRequirementsReview && (
          <Container
            {...MODULE_PROPS}
            className="requirements-review-container"
          >
            <ContainerHeader
              justifyContent="flex-start"
              gap={1}
              alignItems="center"
            >
              <ReqsReviewFeatureIcon color="feature.secondary" boxSize={3} />
              <Heading size="lg">Review items</Heading>
            </ContainerHeader>
            <ContainerBody
              flex="1 1 100%"
              overflowY="auto"
              p={0}
              flexDirection="column"
            >
              {isLoadingActionsDashboard ? (
                <Center p={2}>
                  <Spinner size="xl" />
                </Center>
              ) : (
                <ReqsReviewActionsList
                  items={actionsDashboardResult?.results ?? []}
                />
              )}
            </ContainerBody>
            <ContainerFooter justifyContent="flex-end">
              <Pagination
                totalItems={actionsDashboardResult?.count ?? 0}
                itemsPerPage={requirementsReviewPageSize}
                currentPage={requirementsReviewPage}
                onChangePage={(page) => setRequirementsReviewPage(page)}
              />
            </ContainerFooter>
          </Container>
        )}
      </Box>
    </VStack>
  );
};

export default SingleSiteDashboard;
